import { useState } from 'react'

import api from 'common/api'
import { CollegeOption } from 'components/CollegeAutocomplete'

import Confirmation from './Confirmation'
import RequestAOAccess, { AccessOption } from './RequestAOAccess'
import SubmitHubSpotTicket from './SubmitHubSpotTicket'
import ValidateEmail from './ValidateEmail'

const VALIDATION_FAILURES = ['school_not_found', 'email_domain_mismatch', 'college_not_eligible'] as const
type ValidationFailure = (typeof VALIDATION_FAILURES)[number]

const REQUEST_PENDING = 'request_pending'

const isValidationFailure = (value: unknown): value is ValidationFailure =>
  VALIDATION_FAILURES.includes(value as ValidationFailure)

const getErrorCode = async (err: unknown): Promise<string> => {
  if (!(err instanceof Response)) return ''
  return (await err.json().catch(() => null))?.code ?? ''
}

const CollegeRepRequest = () => {
  const [email, setEmail] = useState('')
  const [college, setCollege] = useState<CollegeOption | null>(null)
  const [accessType, setAccessType] = useState<AccessOption | null>(null)
  const [showValidateEmail, setShowValidateEmail] = useState(false)
  const [showHubSpotForm, setShowHubSpotForm] = useState(false)
  const [showRequestPending, setShowRequestPending] = useState(false)
  const [validationError, setValidationError] = useState<ValidationFailure | ''>('')

  const handleRequestAOAccess = (values: {
    email: string
    selectedCollege: CollegeOption
    accessType: AccessOption
    recaptchaResponse: string
  }) => {
    setEmail(values.email)
    setCollege(values.selectedCollege)
    setAccessType(values.accessType)

    // If the user selects "I don't see my school", we will show the HubSpot form right away.
    if (values.selectedCollege.scid === 0) {
      setValidationError('school_not_found')
      setShowHubSpotForm(true)
      return
    }

    const payload = {
      Email: values.email,
      Scid: values.selectedCollege.scid,
      AccessType: values.accessType,
      RecaptchaResponse: values.recaptchaResponse,
    }

    api
      .post('/api/admissions/register/invite', payload)
      .then((res) => {
        console.log('Email sent:', res)
        setShowValidateEmail(true)
      })
      .catch(async (err) => {
        const code = await getErrorCode(err)
        if (code === REQUEST_PENDING) {
          setShowRequestPending(true)
          return
        }
        setValidationError(isValidationFailure(code) ? code : '')
        setShowHubSpotForm(true)
      })
  }

  if (showRequestPending) {
    return (
      <Confirmation
        pending
        collegeName={college?.name}
      />
    )
  }

  if (showHubSpotForm && email && college && accessType) {
    return (
      <SubmitHubSpotTicket
        email={email}
        selectedCollege={college}
        accessType={accessType}
        validationError={validationError}
      />
    )
  }

  if (showValidateEmail) {
    const handleResend = () =>
      api.post('/api/admissions/register/invite/resend', {
        Email: email,
        Scid: college?.scid,
        AccessType: accessType,
      })

    return <ValidateEmail onResend={handleResend} />
  }

  return <RequestAOAccess onSubmit={handleRequestAOAccess} />
}

export default CollegeRepRequest
